import { supabase } from '../lib/supabase';

class PromoService {
  /**
   * Get promotional website settings
   * @returns {Promise<Object>} Promotional settings object
   */
  async getPromoSettings() {
    try {
      const { data, error } = await supabase?.from('system_settings')?.select('setting_key, setting_value')?.in('setting_key', [
          'promo_hero_section',
          'promo_features',
          'promo_pricing',
          'promo_testimonials',
          'promo_faqs',
          'promo_contact',
          'promo_stats_display',
          'promo_slideshow_images',
          'promo_hero_video'
        ]);

      if (error) throw error;

      const settings = {};
      data?.forEach(row => {
        const key = row?.setting_key?.replace('promo_', '');
        // Ensure setting_value is properly parsed if it's a string
        let value = row?.setting_value;
        
        // If value is a string that looks like JSON, parse it
        if (typeof value === 'string' && (value?.startsWith('[') || value?.startsWith('{'))) {
          try {
            value = JSON.parse(value);
          } catch (e) {
            console.warn(`Failed to parse JSON for ${row?.setting_key}:`, e);
          }
        }
        
        settings[key] = value;
      });

      // If slideshow_images not in settings or empty, fetch from storage bucket
      if (!settings?.slideshow_images || !Array.isArray(settings?.slideshow_images) || settings?.slideshow_images?.length === 0) {
        const storageImages = await this.getHeroSlideshowImagesFromStorage();
        settings.slideshow_images = storageImages;
      }

      // Ensure hero_video has proper structure
      if (!settings?.hero_video || typeof settings?.hero_video !== 'object') {
        settings.hero_video = { url: '', thumbnail_url: '' };
      }

      return {
        success: true,
        data: settings
      };
    } catch (error) {
      console.error('Error fetching promo settings:', error);
      return {
        success: false,
        error: error?.message || 'Failed to fetch promotional settings'
      };
    }
  }

  /**
   * Get hero slideshow images from Supabase storage bucket
   * @returns {Promise<Array>} Array of image objects with url and alt text
   */
  async getHeroSlideshowImagesFromStorage() {
    try {
      // List all files in hero-slideshow-images bucket
      const { data: files, error } = await supabase?.storage?.from('hero-slideshow-images')?.list('', {
          limit: 100,
          sortBy: { column: 'name', order: 'asc' }
        });

      if (error) {
        console.error('Error listing slideshow images:', error);
        return [];
      }

      if (!files || files?.length === 0) {
        return [];
      }

      // Get public URLs for each image
      const images = files?.map(file => {
        const { data: urlData } = supabase?.storage?.from('hero-slideshow-images')?.getPublicUrl(file?.name);
        
        return {
          url: urlData?.publicUrl,
          alt: `Hero slideshow image - ${file?.name?.replace(/\.[^/.]+$/, '')?.replace(/-/g, ' ')}`,
          caption: ''
        };
      })?.filter(img => img?.url); // Filter out any failed URL generations

      return images;

    } catch (error) {
      console.error('Error fetching slideshow images from storage:', error);
      return [];
    }
  }

  async updatePromoSetting(settingKey, value) {
    try {
      const fullKey = settingKey?.startsWith('promo_') ? settingKey : `promo_${settingKey}`;
      
      // Ensure value is properly stringified if it's an object or array
      let settingValue = value;
      if (typeof value === 'object') {
        settingValue = value; // Supabase handles JSONB automatically
      }
      
      // Check if setting exists
      const { data: existing } = await supabase?.from('system_settings')?.select('id')?.eq('setting_key', fullKey)?.single();

      if (existing) {
        // Update existing setting
        const { error } = await supabase?.from('system_settings')?.update({
            setting_value: settingValue,
            updated_at: new Date()?.toISOString()
          })?.eq('setting_key', fullKey);

        if (error) throw error;
      } else {
        // Insert new setting
        const { error } = await supabase?.from('system_settings')?.insert({
            setting_key: fullKey,
            setting_value: settingValue,
            description: `Promotional website ${settingKey?.replace('_', ' ')}`
          });

        if (error) throw error;
      }

      return {
        success: true,
        message: 'Setting updated successfully'
      };
    } catch (error) {
      console.error('Error updating promo setting:', error);
      return {
        success: false,
        error: error?.message || 'Failed to update setting'
      };
    }
  }

  /**
   * Get dynamic statistics for promotional page
   * @returns {Promise<Object>} Statistics object with counts
   */
  async getPromoStatistics() {
    try {
      // Get counts from database in parallel
      const [songsResult, musicalsResult, talentResult] = await Promise.all([
        supabase?.from('songs')?.select('id', { count: 'exact', head: true }),
        supabase?.from('musicals')?.select('id', { count: 'exact', head: true }),
        supabase?.from('talent_profiles')?.select('id', { count: 'exact', head: true })
      ]);

      const stats = {
        songCount: songsResult?.count || 0,
        musicalCount: musicalsResult?.count || 0,
        talentCount: talentResult?.count || 0
      };

      return { success: true, data: stats, error: null };
    } catch (error) {
      console.error('Error fetching promotional statistics:', error);
      return { 
        success: false, 
        data: { songCount: 0, musicalCount: 0, talentCount: 0 }, 
        error: error?.message 
      };
    }
  }

  /**
   * Get featured musicals for promotional showcase
   * @param {number} limit - Number of musicals to fetch
   * @returns {Promise<Object>} Featured musicals
   */
  async getFeaturedMusicals(limit = 6) {
    try {
      const { data, error } = await supabase?.from('musicals')?.select('id, title, description, poster_url, year_premiered, composer')?.eq('is_active', true)?.order('created_at', { ascending: false })?.limit(limit);

      if (error) throw error;

      return { success: true, data: data || [], error: null };
    } catch (error) {
      console.error('Error fetching featured musicals:', error);
      return { success: false, data: [], error: error?.message };
    }
  }

  /**
   * Get featured talent profiles for promotional showcase
   * @param {number} limit - Number of talent profiles to fetch
   * @returns {Promise<Object>} Featured talent profiles with validated images
   */
  async getFeaturedTalent(limit = 8) {
    try {
      const { data, error } = await supabase?.from('talent_profiles')?.select('id, first_name, last_name, display_name, headshot_url, instagram, website, youtube, tiktok')?.order('updated_at', { ascending: false })?.limit(limit);

      if (error) throw error;

      // Process talent data with proper public URL handling
      const processedTalent = (data || [])?.map((talent) => {
        let headshotUrl = talent?.headshot_url;
        
        // Check if headshot_url is empty, undefined, or just whitespace
        if (!headshotUrl || typeof headshotUrl !== 'string' || headshotUrl?.trim() === '') {
          headshotUrl = null;
        } else {
          // If it's a storage path (not a full URL), generate public URL
          if (!headshotUrl?.startsWith('http')) {
            // Use profile-images bucket which is now public
            const { data: publicUrlData } = supabase?.storage
              ?.from('profile-images')
              ?.getPublicUrl(headshotUrl);
            
            headshotUrl = publicUrlData?.publicUrl || null;
          }
          
          // Final validation: check if it's a valid URL format
          if (headshotUrl) {
            try {
              new URL(headshotUrl);
            } catch (e) {
              console.warn(`Invalid headshot URL for talent ${talent?.id}:`, headshotUrl);
              headshotUrl = null;
            }
          }
        }
        
        return {
          ...talent,
          headshot_url: headshotUrl
        };
      });

      return { success: true, data: processedTalent, error: null };
    } catch (error) {
      console.error('Error fetching featured talent:', error);
      return { success: false, data: [], error: error?.message };
    }
  }

  /**
   * Get active signup passcode requirement status
   * @returns {Promise<Object>} Passcode requirement status
   */
  async getPasscodeRequirement() {
    try {
      const { data, error } = await supabase?.from('signup_passcodes')?.select('is_required, passcode')?.order('created_at', { ascending: false })?.limit(1)?.single();

      if (error && error?.code !== 'PGRST116') throw error; // PGRST116 = no rows

      return { 
        success: true, 
        data: data || { is_required: false, passcode: null }, 
        error: null 
      };
    } catch (error) {
      console.error('Error fetching passcode requirement:', error);
      return { 
        success: false, 
        data: { is_required: false, passcode: null }, 
        error: error?.message 
      };
    }
  }

  /**
   * Validate signup passcode
   * @param {string} passcode - User-provided passcode
   * @returns {Promise<Object>} Validation result
   */
  async validateSignupPasscode(passcode) {
    try {
      const { data, error } = await supabase?.from('signup_passcodes')?.select('passcode, is_required')?.eq('passcode', passcode)?.eq('is_required', true)?.single();

      if (error && error?.code !== 'PGRST116') throw error;

      const isValid = !!data;
      return { success: true, data: { isValid }, error: null };
    } catch (error) {
      console.error('Error validating passcode:', error);
      return { success: false, data: { isValid: false }, error: error?.message };
    }
  }

  /**
   * Validate and apply coupon code
   * @param {string} couponCode - User-provided coupon code
   * @returns {Promise<Object>} Coupon validation and discount info
   */
  async validateCouponCode(couponCode) {
    try {
      const { data, error } = await supabase?.from('coupon_codes')?.select('code, discount_percentage, is_active, expiration_date, usage_count, usage_limit')?.eq('code', couponCode?.toUpperCase())?.eq('is_active', true)?.single();

      if (error && error?.code !== 'PGRST116') throw error;

      if (!data) {
        return { 
          success: false, 
          data: { isValid: false, discount: 0 }, 
          error: 'Invalid coupon code' 
        };
      }

      // Check expiration
      if (data?.expiration_date && new Date(data?.expiration_date) < new Date()) {
        return { 
          success: false, 
          data: { isValid: false, discount: 0 }, 
          error: 'Coupon has expired' 
        };
      }

      // Check usage limit
      if (data?.usage_limit && data?.usage_count >= data?.usage_limit) {
        return { 
          success: false, 
          data: { isValid: false, discount: 0 }, 
          error: 'Coupon usage limit reached' 
        };
      }

      return { 
        success: true, 
        data: { 
          isValid: true, 
          discount: data?.discount_percentage,
          code: data?.code
        }, 
        error: null 
      };
    } catch (error) {
      console.error('Error validating coupon:', error);
      return { 
        success: false, 
        data: { isValid: false, discount: 0 }, 
        error: error?.message 
      };
    }
  }

  /**
   * Get complete talent detail for promotional page (public access)
   * Includes bio, roles, and associated musicals with posters
   */
  async getTalentDetailForPromo(talentId) {
    try {
      if (!talentId) {
        throw new Error('Talent ID is required');
      }

      // Get talent basic info with bio and headshot
      const { data: talent, error: talentError } = await supabase?.from('talent_profiles')?.select('id, first_name, last_name, display_name, bio, headshot_url, instagram, website, youtube, tiktok, email, phone')?.eq('id', talentId)?.single();

      if (talentError) throw talentError;

      if (!talent) {
        return {
          success: false,
          error: 'Talent not found'
        };
      }

      // Get roles directly assigned to this talent
      const { data: assignedRoles, error: assignedRolesError } = await supabase?.from('roles')?.select(`
          id,
          character_name,
          description,
          voice_type,
          vocal_range,
          role_type,
          age_range,
          is_principal,
          musical_id,
          musicals (
            id,
            title,
            poster_url,
            composer,
            lyricist,
            year_premiered
          )
        `)?.eq('assigned_talent_id', talentId)?.order('character_name');

      if (assignedRolesError) throw assignedRolesError;

      // Also get songs associated with this talent
      const { data: songs, error: songsError } = await supabase?.from('songs')?.select(`
          id, 
          title, 
          musical_id,
          musicals (
            id,
            title,
            poster_url
          )
        `)?.eq('talent_id', talentId);

      if (songsError) throw songsError;

      // Format roles with musical info
      const roles = assignedRoles?.map(role => ({
        ...role,
        musical: role?.musicals
      })) || [];

      // Format songs with musical info
      const formattedSongs = songs?.map(song => ({
        id: song?.id,
        title: song?.title,
        musical: song?.musicals
      })) || [];

      // Extract unique musicals from roles and songs
      const musicalMap = new Map();
      
      roles?.forEach(role => {
        if (role?.musical && !musicalMap?.has(role?.musical?.id)) {
          musicalMap?.set(role?.musical?.id, role?.musical);
        }
      });
      
      formattedSongs?.forEach(song => {
        if (song?.musical && !musicalMap?.has(song?.musical?.id)) {
          musicalMap?.set(song?.musical?.id, song?.musical);
        }
      });

      const musicals = Array?.from(musicalMap?.values());

      // Use database bio if available, otherwise generate fallback
      let bio = talent?.bio || '';
      if (!bio && roles?.length > 0) {
        const roleNames = roles?.slice(0, 3)?.map(r => r?.character_name)?.join(', ');
        const musicalNames = [...new Set(roles?.map(r => r?.musical?.title))]?.slice(0, 2)?.join(' and ');
        bio = `Professional musical theatre performer specializing in roles such as ${roleNames}${roles?.length > 3 ? ' and more' : ''}. Teaching expertise includes ${musicalNames}${musicals?.length > 2 ? ' and other productions' : ''}.`;
      } else if (!bio) {
        bio = 'Experienced musical theatre professional and educator dedicated to helping students master their craft.';
      }

      return {
        success: true,
        data: {
          ...talent,
          bio,
          roles,
          songs: formattedSongs,
          musicals
        }
      };

    } catch (error) {
      console.error('Error fetching talent detail for promo:', error);
      return {
        success: false,
        error: error?.message || 'Failed to fetch talent details'
      };
    }
  }

  /**
   * Get roles taught by a specific talent
   */
  async getTalentRoles(talentId) {
    try {
      if (!talentId) {
        throw new Error('Talent ID is required');
      }

      // Find songs by this talent and get associated roles
      const { data: songs, error: songsError } = await supabase?.from('songs')?.select('id')?.eq('talent_id', talentId);

      if (songsError) throw songsError;

      if (!songs || songs?.length === 0) {
        return { success: true, data: [] };
      }

      const songIds = songs?.map(s => s?.id);

      // Get roles associated with these songs through character_song_analyses
      const { data: analyses, error: analysesError } = await supabase?.from('character_song_analyses')?.select('role_id')?.in('song_id', songIds);

      if (analysesError) throw analysesError;

      if (!analyses || analyses?.length === 0) {
        return { success: true, data: [] };
      }

      const roleIds = [...new Set(analyses?.map(a => a?.role_id)?.filter(Boolean))];

      // Get role details
      const { data: roles, error: rolesError } = await supabase?.from('roles')?.select('id, character_name, voice_type, role_type')?.in('id', roleIds)?.order('character_name');

      if (rolesError) throw rolesError;

      return {
        success: true,
        data: roles || []
      };

    } catch (error) {
      console.error('Error fetching talent roles:', error);
      return {
        success: false,
        error: error?.message || 'Failed to fetch talent roles'
      };
    }
  }
}

export default new PromoService();